import { useCallback, useEffect, useState } from "react";
import makeStyles from "@mui/styles/makeStyles";
import { ClientsDropdown, DatetimeDropdown, ProjectsDropdown } from "../DropdownV2";
import Button from "@mui/material/Button";
import Collapse from "@mui/material/Collapse";
import Divider from "@mui/material/Divider";
import Popover from "@mui/material/Popover";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { calibrationService, DEFAULT_GROUP_KEY } from "../../../../services/CalibrationService";
import SensorCountsContent from "./SensorCountsContent";

const SENSOR_COUNT_ANALYSIS_DATASET_TYPE = "sensor-count-analysis";
const SPAN_PALM_COEFFICIENT_DATASET_TYPE = "span-palm-coefficients";
const SPAN_PIT_COEFFICIENT_DATASET_TYPE = "span-pit-coefficients";
const ROOT_GEOJSON_DATASET_TYPE = "root-geojson";

const useStyles = makeStyles(() => ({
    appContainer: {
        display: "flex",
        flexDirection: "column",
        height: "100%",
    },
    toolbar: {
        display: "flex",
        alignItems: "center",
        gap: "16px",
        padding: "0 12px",
        borderBottom: "1px solid #e0e0e0",
        flexShrink: 0,
    },
    popoverContent: {
        display: "flex",
        flexDirection: "column",
        gap: "12px",
        padding: "12px",
        minWidth: "640px",
    },
    clientProjectRow: {
        display: "flex",
        flexDirection: "row",
        gap: "12px",
    },
    collapseHeader: {
        display: "flex",
        alignItems: "center",
        cursor: "pointer",
        userSelect: "none",
        gap: "4px",
    },
    buildInputsContent: {
        display: "flex",
        flexDirection: "column",
        gap: "12px",
        paddingTop: "8px",
    },
    dropdownRow: {
        display: "flex",
        flexDirection: "row",
        gap: "12px",
    },
    mainContent: {
        flexGrow: 1,
        display: "flex",
        flexDirection: "column",
        overflow: "auto",
        padding: "12px",
    },
}));

export default function SensorCounts() {
    const classes = useStyles();
    const [client, setClient] = useState("");
    const [project, setProject] = useState("");
    const [isPopoverOpen, setIsPopoverOpen] = useState(false);
    const [buttonEl, setButtonEl] = useState(null);
    const [buildInputsExpanded, setBuildInputsExpanded] = useState(false);
    const [coefficientType, setCoefficientType] = useState(SPAN_PALM_COEFFICIENT_DATASET_TYPE);
    const [coefficientVersion, setCoefficientVersion] = useState(null);
    const [rootGeojsonVersion, setRootGeojsonVersion] = useState(null);
    const [dataset, setDataset] = useState(null);
    const [datasetVersionDttm, setDatasetVersionDttm] = useState(null);
    const [sensorPlacementInputData, setSensorPlacementInputData] = useState(null);
    const [datasetLoading, setDatasetLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const buttonCallbackRef = useCallback((node) => setButtonEl(node), []);

    const handleSetClient = useCallback((value) => {
        setClient(value);
        setProject("");
        setDataset(null);
    }, []);

    const handleSetProject = useCallback(
        (value) => {
            setProject(value);
            if (value) setIsPopoverOpen(false);
        },
        []
    );

    useEffect(() => {
        if (!client || !project) {
            setDataset(null);
            setDatasetVersionDttm(null);
            setSensorPlacementInputData(null);
            return;
        }
        setDatasetLoading(true);
        Promise.all([
            calibrationService.getLatestDatasetViaSignedUrl(client, project, DEFAULT_GROUP_KEY, SENSOR_COUNT_ANALYSIS_DATASET_TYPE, false),
            calibrationService.getLatestDatasetViaSignedUrl(client, project, DEFAULT_GROUP_KEY, "sensor-placement-input", false),
        ])
            .then(([analysisResponse, sensorPlacementInputResponse]) => {
                setDataset(analysisResponse?.data ?? null);
                setDatasetVersionDttm(analysisResponse?.meta?.version_dttm ?? null);
                setSensorPlacementInputData(sensorPlacementInputResponse ?? null);
            })
            .catch(() => {
                setDataset(null);
                setDatasetVersionDttm(null);
                setSensorPlacementInputData(null);
            })
            .finally(() => setDatasetLoading(false));
    }, [client, project]);

    const handleCoefficientTypeChange = (_, newType) => {
        if (newType) {
            setCoefficientType(newType);
            setCoefficientVersion(null);
        }
    };

    const generateValid = client && project && coefficientVersion && rootGeojsonVersion;

    const handleGenerate = async () => {
        setSubmitting(true);
        const inputs = [
            { dataset_type: ROOT_GEOJSON_DATASET_TYPE, group_key: DEFAULT_GROUP_KEY, version_dttm: rootGeojsonVersion },
            { dataset_type: coefficientType, group_key: DEFAULT_GROUP_KEY, version_dttm: coefficientVersion },
        ];
        try {
            await calibrationService.triggerAspenInputPipeline(client, project, { phases: [1, 2] }, inputs);
        } finally {
            setSubmitting(false);
        }
    };

    const reloadDataset = useCallback(() => {
        if (!client || !project) return;
        setDatasetLoading(true);
        calibrationService
            .getLatestDatasetViaSignedUrl(client, project, DEFAULT_GROUP_KEY, SENSOR_COUNT_ANALYSIS_DATASET_TYPE, false)
            .then((data) => {
                setDataset(data?.data ?? null);
                setDatasetVersionDttm(data?.meta?.version_dttm ?? null);
            })
            .catch(() => {
                setDataset(null);
                setDatasetVersionDttm(null);
            })
            .finally(() => setDatasetLoading(false));
    }, [client, project]);


    const popoverOpen = isPopoverOpen || !project;
    const buttonLabel = client ? `${client} / ${project || "..."}` : "Select Client / Project";

    return (
        <div className={classes.appContainer}>
            <div className={classes.toolbar}>
                <Button
                    ref={buttonCallbackRef}
                    variant="outlined"
                    size="small"
                    endIcon={<KeyboardArrowDownIcon />}
                    onClick={() => setIsPopoverOpen((prev) => !prev)}
                >
                    {buttonLabel}
                </Button>
            </div>
            <Popover
                open={popoverOpen}
                anchorEl={buttonEl}
                onClose={() => setIsPopoverOpen(false)}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
                disableRestoreFocus
                keepMounted
                sx={{ pointerEvents: "none" }}
                PaperProps={{ sx: { pointerEvents: "auto" } }}
            >
                <div className={classes.popoverContent}>
                    <div className={classes.clientProjectRow}>
                        <ClientsDropdown value={client} setValue={handleSetClient} styleClass={{ flex: 1 }} />
                        <ProjectsDropdown
                            clientName={client}
                            value={project}
                            setValue={handleSetProject}
                            styleClass={{ flex: 1 }}
                        />
                    </div>
                    <Divider />
                    <div
                        className={classes.collapseHeader}
                        onClick={() => setBuildInputsExpanded((prev) => !prev)}
                    >
                        {buildInputsExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                        <Typography variant="body2" style={{ fontWeight: "bold" }}>
                            (Re)Build Analysis Inputs
                        </Typography>
                    </div>
                    <Collapse in={buildInputsExpanded}>
                        <div className={classes.buildInputsContent}>
                            <ToggleButtonGroup
                                exclusive
                                value={coefficientType}
                                onChange={handleCoefficientTypeChange}
                                size="small"
                            >
                                <ToggleButton value={SPAN_PALM_COEFFICIENT_DATASET_TYPE}>
                                    Span Palm Coefficients
                                </ToggleButton>
                                <ToggleButton value={SPAN_PIT_COEFFICIENT_DATASET_TYPE}>
                                    Span PIT Coefficients
                                </ToggleButton>
                            </ToggleButtonGroup>
                            <div className={classes.dropdownRow}>
                                <DatetimeDropdown
                                    key={coefficientType}
                                    clientName={client}
                                    projectName={project}
                                    datasetType={coefficientType}
                                    group={DEFAULT_GROUP_KEY}
                                    value={coefficientVersion}
                                    setValue={setCoefficientVersion}
                                    name={
                                        coefficientType === SPAN_PALM_COEFFICIENT_DATASET_TYPE
                                            ? "Span Palm Coefficients Version"
                                            : "Span PIT Coefficients Version"
                                    }
                                    styleClass={{ flex: 1 }}
                                />
                                <DatetimeDropdown
                                    clientName={client}
                                    projectName={project}
                                    datasetType={ROOT_GEOJSON_DATASET_TYPE}
                                    group={DEFAULT_GROUP_KEY}
                                    value={rootGeojsonVersion}
                                    setValue={setRootGeojsonVersion}
                                    name="Root Geojson Version"
                                    styleClass={{ flex: 1 }}
                                />
                            </div>
                            <Button
                                variant="contained"
                                size="small"
                                disabled={!generateValid || submitting}
                                onClick={handleGenerate}
                            >
                                {submitting ? "Submitting..." : "Generate"}
                            </Button>
                        </div>
                    </Collapse>
                </div>
            </Popover>
            <div className={classes.mainContent}>
                <SensorCountsContent
                    client={client}
                    project={project}
                    dataset={dataset}
                    datasetVersionDttm={datasetVersionDttm}
                    sensorPlacementInputData={sensorPlacementInputData}
                    datasetLoading={datasetLoading}
                    onDatasetReload={reloadDataset}
                />
            </div>
        </div>
    );
}
